import clamp from 'lodash/clamp';
import React from 'react';

interface UseScalingProps {
  imageUrl: string | undefined;
  minWidth: number;
  minHeight: number;
  cropBoxWidth: number;
  cropBoxHeight: number;
}

interface UseScalingState {
  scale: number;
  scaleMin: number;
  scaleMax: number;
  isScaleRangeCalculating: boolean;
}

type UseScalingReturn = [UseScalingState, { setScale: (value: number) => void }];

export const SCALE_DEFAULT = 1.3;
export const SCALE_MIN_DEFAULT = 1.0;
export const SCALE_MAX_DEFAULT = 3.0;

const getDefaultState = ({ imageUrl }: Pick<UseScalingProps, 'imageUrl'>): UseScalingState => ({
  scaleMin: SCALE_MIN_DEFAULT,
  scaleMax: SCALE_MAX_DEFAULT,
  scale: SCALE_DEFAULT,
  isScaleRangeCalculating: Boolean(imageUrl),
});

export const useScaling = ({ imageUrl, minWidth, minHeight, cropBoxWidth, cropBoxHeight }: UseScalingProps): UseScalingReturn => {
  const [state, setState] = React.useState<UseScalingState>(getDefaultState({ imageUrl }));

  React.useEffect(() => {
    let isCurrentEffectInstanceActive = true;

    (async () => {
      const draftState = getDefaultState({ imageUrl });

      setState({ ...draftState });

      if (!imageUrl) {
        return;
      }

      const image = new Image();
      const imageLoadingPromise = new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
      });
      image.crossOrigin = 'anonymous';
      image.src = imageUrl;

      try {
        await imageLoadingPromise;
      } catch (err: unknown) {
        // Image loading error
        console.error(err);
        draftState.isScaleRangeCalculating = false;
        setState({ ...draftState });
      }

      if (!isCurrentEffectInstanceActive) {
        return;
      }

      const reserveMargin = 10;
      const newScaleMaxX = image.width / (minWidth + reserveMargin);
      const newScaleMaxY = image.height / (minHeight + reserveMargin);
      const newScaleMinX = cropBoxWidth / image.width;
      const newScaleMinY = cropBoxHeight / image.height;

      draftState.scaleMin = Math.max(newScaleMinX, newScaleMinY);
      draftState.scaleMax = Math.max(SCALE_MIN_DEFAULT, Math.min(newScaleMaxX, newScaleMaxY, SCALE_MAX_DEFAULT));
      draftState.scale = clamp(draftState.scale, draftState.scaleMin, draftState.scaleMax);
      draftState.isScaleRangeCalculating = false;

      setState({ ...draftState });
    })();

    return () => {
      isCurrentEffectInstanceActive = false;
    };
  }, [cropBoxHeight, cropBoxWidth, imageUrl, minHeight, minWidth]);

  const setScale = React.useCallback<UseScalingReturn[1]['setScale']>((value) => {
    setState((prevState) => {
      const newScale = clamp(value, prevState.scaleMin, prevState.scaleMax);

      if (prevState.scale === newScale) {
        return prevState;
      }

      return {
        ...prevState,
        scale: newScale,
      };
    });
  }, []);

  return [state, { setScale }];
};
